<template>
  <Teleport to="body">
    <div v-if="openState" class="chat-upload-manager" role="dialog" aria-modal="true" :aria-label="labels.upload_title" @click.self="close" @keydown.esc="close">
      <section class="chat-upload-manager__panel">
        <header class="chat-upload-manager__header">
          <h2>{{ labels.upload_title }}</h2>
          <button ref="closeButton" type="button" :aria-label="labels.close" :disabled="deleting" @click="close"><i class="fas fa-times" aria-hidden="true"></i></button>
        </header>
        <div class="chat-upload-manager__body">
          <p v-if="loadError && uploads.length === 0" class="chat-upload-manager__status" role="alert">{{ labels.upload_load_failed }}</p>
          <p v-else-if="!loading && uploads.length === 0" class="chat-upload-manager__status">{{ labels.upload_empty }}</p>
          <div v-else class="chat-upload-manager__grid">
            <button v-for="upload in uploads" :key="upload.id" type="button" class="chat-upload-manager__image" :class="{'chat-upload-manager__image--selected': selected.has(upload.id)}" :disabled="deleting" :aria-pressed="selected.has(upload.id)" @click="toggle(upload.id)">
              <span v-if="failedImages.has(upload.id)" class="chat-upload-manager__image-error"><i class="fas fa-image" aria-hidden="true"></i></span>
              <img v-else :src="upload.image_url" :alt="labels.image_alt" @error="markFailed(upload.id)">
              <span class="chat-upload-manager__check" aria-hidden="true"><i class="fas fa-check"></i></span>
            </button>
          </div>
          <div ref="sentinel" class="chat-upload-manager__sentinel" aria-hidden="true"></div>
          <div v-if="loading" class="chat-upload-manager__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
        </div>
        <div class="chat-upload-manager__actions">
          <span>{{ countLabel }}</span>
          <button type="button" :disabled="selected.size === 0 || deleting" @click="openDeleteConfirm">{{ labels.upload_delete }}</button>
        </div>
      </section>
    </div>
    <div v-if="confirmOpen" class="chat-send-modal" role="dialog" aria-modal="true" :aria-label="labels.upload_delete" @keydown.esc="closeDeleteConfirm">
      <div class="chat-send-modal__panel">
        <div v-if="deleting" class="chat-send-modal__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
        <p class="chat-send-modal__message">{{ labels.upload_delete_confirm }}</p>
        <div class="chat-send-modal__actions"><button type="button" :disabled="deleting" @click="closeDeleteConfirm">{{ labels.close }}</button><button ref="deleteButton" type="button" :disabled="deleting" @click="remove">{{ labels.upload_delete_execute }}</button></div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, ref} from 'vue';

const props = defineProps({
  labels: {type: Object, required: true},
  listApiUrl: {type: String, required: true},
  deleteApiUrl: {type: String, required: true},
  deleteCsrfToken: {type: String, required: true},
  deleteLimit: {type: Number, required: true},
  listLoader: {type: Function, default: null},
  deleteLoader: {type: Function, default: null},
});
const emit = defineEmits(['notify', 'deleted', 'closed']);
const openState = ref(false);
const confirmOpen = ref(false);
const uploads = ref([]);
const selected = ref(new Set());
const failedImages = ref(new Set());
const nextCursor = ref(null);
const hasNext = ref(false);
const loading = ref(false);
const deleting = ref(false);
const loadError = ref(false);
const closeButton = ref(null);
const deleteButton = ref(null);
const sentinel = ref(null);
let observer = null;
let returnFocus = null;

const replaceTokens = (template, values) => Object.entries(values).reduce((result, [key, value]) => result.split(`%${key}%`).join(String(value)), template);
const countLabel = computed(() => replaceTokens(props.labels.upload_selected_count, {count: selected.value.size, limit: props.deleteLimit}));
const validResult = (response) => response?.status === 'OK' && response.result !== null && typeof response.result === 'object';

const requestList = (cursor = null) => props.listLoader !== null ? props.listLoader(cursor) : $.ajax({
  url: props.listApiUrl, method: 'POST', contentType: 'application/json; charset=UTF-8', dataType: 'json', data: JSON.stringify(cursor === null ? {} : {cursor}),
});
const requestDelete = (ids) => props.deleteLoader !== null ? props.deleteLoader(ids) : $.ajax({
  url: props.deleteApiUrl, method: 'DELETE', contentType: 'application/json; charset=UTF-8', dataType: 'json', data: JSON.stringify({upload_ids: ids, _token: props.deleteCsrfToken}),
});
const reset = () => {
  uploads.value = [];
  selected.value = new Set();
  failedImages.value = new Set();
  nextCursor.value = null;
  hasNext.value = false;
  loadError.value = false;
};
const load = async (cursor = null) => {
  if (loading.value || deleting.value || (cursor !== null && !hasNext.value)) return;
  loading.value = true;
  loadError.value = false;
  try {
    const response = await requestList(cursor);
    if (!validResult(response) || !Array.isArray(response.result.uploads)) throw new Error('Invalid upload list response.');
    uploads.value = cursor === null ? response.result.uploads : [...uploads.value, ...response.result.uploads];
    nextCursor.value = response.result.next_cursor ?? null;
    hasNext.value = response.result.has_next === true;
  } catch {
    loadError.value = true;
    emit('notify', {type: 'error', message: props.labels.upload_load_failed});
  } finally {
    loading.value = false;
  }
};
const observe = () => {
  observer?.disconnect();
  if (typeof IntersectionObserver === 'undefined' || sentinel.value === null) return;
  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) load(nextCursor.value);
  }, {root: document.querySelector('.chat-upload-manager__body'), rootMargin: '120px'});
  observer.observe(sentinel.value);
};
const open = async (trigger = document.activeElement) => {
  returnFocus = trigger;
  reset();
  openState.value = true;
  await nextTick();
  closeButton.value?.focus();
  observe();
  await load();
};
const close = async () => {
  if (deleting.value) return;
  observer?.disconnect();
  openState.value = false;
  confirmOpen.value = false;
  await nextTick();
  returnFocus?.focus?.();
  emit('closed');
};
const toggle = (id) => {
  const values = new Set(selected.value);
  if (values.has(id)) values.delete(id);
  else if (values.size >= props.deleteLimit) {
    emit('notify', {type: 'error', message: props.labels.upload_selection_limit});
    return;
  } else values.add(id);
  selected.value = values;
};
const openDeleteConfirm = async () => {
  if (selected.value.size === 0 || deleting.value) return;
  confirmOpen.value = true;
  await nextTick();
  deleteButton.value?.focus();
};
const closeDeleteConfirm = async () => {
  if (deleting.value) return;
  confirmOpen.value = false;
  await nextTick();
  closeButton.value?.focus();
};
const markFailed = (id) => { failedImages.value = new Set([...failedImages.value, id]); };
const remove = async () => {
  if (deleting.value || selected.value.size === 0) return;
  deleting.value = true;
  try {
    const response = await requestDelete([...selected.value]);
    if (!validResult(response) || !Array.isArray(response.result.deleted_upload_ids)) throw new Error('Invalid upload delete response.');
    const deleted = new Set(response.result.deleted_upload_ids);
    uploads.value = uploads.value.filter((upload) => !deleted.has(upload.id));
    selected.value = new Set([...selected.value].filter((id) => !deleted.has(id)));
    confirmOpen.value = false;
    emit('deleted', [...deleted]);
    emit('notify', {type: 'success', message: props.labels.upload_deleted});
  } catch (error) {
    confirmOpen.value = false;
    if (error?.status === 409) {
      deleting.value = false;
      reset();
      emit('notify', {type: 'error', message: props.labels.upload_state_changed});
      await load();
    } else emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.upload_delete_failed});
  } finally {
    deleting.value = false;
    await nextTick();
    closeButton.value?.focus();
  }
};

defineExpose({open, close});
onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.chat-upload-manager{position:fixed;z-index:8900;inset:0;display:grid;padding:6dvh 5vw;background:rgba(19,25,31,.52);place-items:center}.chat-upload-manager__panel{display:grid;width:min(640px,72vw);max-width:100%;height:min(680px,88dvh);max-height:100%;grid-template-rows:auto minmax(0,1fr) auto;border-radius:18px;background:#fff;box-shadow:0 18px 54px rgba(0,0,0,.28);overflow:hidden}.chat-upload-manager__header{display:grid;align-items:center;min-height:58px;grid-template-columns:44px 1fr 44px;border-bottom:1px solid #e5e6e9}.chat-upload-manager__header h2{grid-column:2;margin:0;font-size:18px;font-weight:650;text-align:center}.chat-upload-manager__header button{width:44px;height:44px;grid-column:3;border:0;background:transparent;font-size:19px}.chat-upload-manager__body{position:relative;min-height:240px;padding:18px;overflow:auto}.chat-upload-manager__grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.chat-upload-manager__image{position:relative;aspect-ratio:1;border:3px solid transparent;border-radius:10px;padding:0;background:#e9ebee;overflow:hidden}.chat-upload-manager__image img{width:100%;height:100%;object-fit:cover}.chat-upload-manager__image--selected{border-color:#16883d}.chat-upload-manager__check{position:absolute;top:7px;right:7px;display:none;width:25px;height:25px;border-radius:50%;color:#fff;background:#16883d;font-size:13px;place-items:center}.chat-upload-manager__image--selected .chat-upload-manager__check{display:grid}.chat-upload-manager__image-error{display:grid;width:100%;height:100%;color:#8a8f97;font-size:28px;place-items:center}.chat-upload-manager__sentinel{height:1px}.chat-upload-manager__loading{display:grid;min-height:72px;place-items:center}.chat-upload-manager__status{margin:70px 16px;color:#747980;text-align:center}.chat-upload-manager__actions{display:flex;align-items:center;justify-content:space-between;gap:16px;min-height:68px;padding:12px 18px;border-top:1px solid #e5e6e9;background:#fff}.chat-upload-manager__actions span{color:#6f747b;font-size:13px}.chat-upload-manager__actions button{min-width:112px;min-height:42px;padding:0 22px;border:0;border-radius:999px;color:#fff;background:#d73a49;font-weight:650}.chat-upload-manager__actions button:disabled{background:#c7c9cd}@media(max-width:767px){.chat-upload-manager{padding:0;place-items:center}.chat-upload-manager__panel{width:92vw;max-width:520px;height:86dvh;max-height:760px;border-radius:16px}.chat-upload-manager__body{padding:10px}.chat-upload-manager__grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.chat-upload-manager__actions{min-height:62px;padding:10px 12px}.chat-upload-manager__actions button{min-width:92px;padding:0 18px}}
</style>
